import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Checkbox,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import CasinoIcon from '@mui/icons-material/Casino';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  QWS_PROPERTY_LABELS,
  buildQualityMatrixTaskSlice,
  generateFreshMatrixSeed,
  parseQWSDataset,
  resolveMatrixSeed,
} from '../../utils/qwsParser';

const PROPERTY_SHORT_LABELS = ['RT', 'AV', 'TH', 'SU', 'RE', 'CO', 'BP', 'LA', 'DOC'] as const;

interface QosMatrixModalProps {
  open: boolean;
  onClose: () => void;
  datasetText: string;
  taskCount: number;
  seed?: number;
  onSeedChange?: (seed: number) => void;
  taskSpecificVariationEnabled?: boolean;
  onTaskSpecificVariationChange?: (enabled: boolean) => void;
  selectedFileName?: string;
}

function formatMatrixValue(value: number): string {
  if (!Number.isFinite(value)) {
    return 'N/A';
  }

  return Math.abs(value) >= 100 ? value.toFixed(2) : value.toFixed(4);
}

function parseSeedInput(value: string): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return resolveMatrixSeed(parsed);
}

const QosMatrixModal: React.FC<QosMatrixModalProps> = ({
  open,
  onClose,
  datasetText,
  taskCount,
  seed,
  onSeedChange,
  taskSpecificVariationEnabled = true,
  onTaskSpecificVariationChange,
  selectedFileName,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [fallbackSeed, setFallbackSeed] = useState(() => resolveMatrixSeed());
  const [seedInput, setSeedInput] = useState('');
  const [taskIndex, setTaskIndex] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const services = useMemo(
    () => (datasetText ? parseQWSDataset(datasetText) : []),
    [datasetText]
  );

  const totalTasks = Math.max(1, taskCount || 1);
  const effectiveSeed = parseSeedInput(seedInput) ?? seed ?? fallbackSeed;
  const seedIsInvalid = seedInput.trim().length > 0 && parseSeedInput(seedInput) === null;
  const seedIsApplied = seed === effectiveSeed;

  const previewRows = useMemo(() => {
    if (!services.length) {
      return [];
    }

    return buildQualityMatrixTaskSlice(
      services,
      taskIndex,
      effectiveSeed,
      taskSpecificVariationEnabled
    ).rows;
  }, [effectiveSeed, services, taskIndex, taskSpecificVariationEnabled]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return previewRows.slice(start, start + rowsPerPage);
  }, [page, previewRows, rowsPerPage]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setTaskIndex((prev) => Math.min(prev, totalTasks - 1));
    setPage(0);
    setSeedInput(String(seed ?? fallbackSeed));
  }, [open, seed, fallbackSeed, totalTasks]);

  useEffect(() => {
    setPage(0);
  }, [taskIndex, effectiveSeed, rowsPerPage]);

  const handleApplySeed = () => {
    const parsedSeed = parseSeedInput(seedInput);
    if (parsedSeed === null) {
      return;
    }

    onSeedChange?.(parsedSeed);
    setFallbackSeed(parsedSeed);
  };

  const handleRandomizeSeed = () => {
    const nextSeed = generateFreshMatrixSeed(seed ?? fallbackSeed);
    setFallbackSeed(nextSeed);
    setSeedInput(String(nextSeed));
    onSeedChange?.(nextSeed);
  };

  const handleTaskStep = (delta: number) => {
    setTaskIndex((prev) => Math.min(Math.max(prev + delta, 0), totalTasks - 1));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          minHeight: fullScreen ? '100%' : '78vh',
          backgroundImage:
            'radial-gradient(circle at top left, rgba(15, 118, 110, 0.08), transparent 32%), linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 2,
        }}
      >
        <GridViewIcon color="primary" />
        <Box>
          <Typography variant="h6" component="div">
            QoS Matrix Seed & Preview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Inspect the task-service QoS slice generated from the uploaded dataset and current seed.
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
        {!datasetText ? (
          <Alert severity="info">Upload a QWS dataset first to preview the QoS matrix.</Alert>
        ) : services.length === 0 ? (
          <Alert severity="warning">
            The uploaded file could not be parsed into QWS rows. Check the CSV format and try again.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box
              sx={{
                borderRadius: 3,
                px: { xs: 2, md: 3 },
                py: { xs: 2.5, md: 3 },
                color: '#f8fafc',
                background:
                  'linear-gradient(135deg, #0f172a 0%, #12324a 52%, #0f766e 100%)',
                boxShadow: '0 18px 40px rgba(15, 23, 42, 0.18)',
              }}
            >
              <Typography variant="overline" sx={{ letterSpacing: 1.5, opacity: 0.78 }}>
                Reproducible Matrix
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                {taskSpecificVariationEnabled
                  ? 'The seed controls the exact QoS variation used for every task.'
                  : 'Raw QWS values are being used for every task.'}
              </Typography>
              <Typography variant="body2" sx={{ maxWidth: 860, opacity: 0.9 }}>
                {taskSpecificVariationEnabled
                  ? 'Keep the same dataset, task count, and seed to regenerate the exact same matrix. Change the seed to rebuild the controlled task-specific variation before evaluation.'
                  : 'Task-specific variation is disabled, so each task sees the original QoS values from the QWS file. The seed no longer changes the matrix values shown here.'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {selectedFileName && (
                  <Chip
                    label={selectedFileName}
                    sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'inherit' }}
                  />
                )}
                <Chip
                  label={`Services ${services.length}`}
                  sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'inherit' }}
                />
                <Chip
                  label={`Tasks ${totalTasks}`}
                  sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'inherit' }}
                />
                <Chip
                  label={taskSpecificVariationEnabled ? `Active Seed ${effectiveSeed}` : 'Fixed Raw QWS'}
                  sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'inherit' }}
                />
              </Box>
            </Box>

            <Grid container spacing={2.5}>
              <Grid item xs={12} lg={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    height: '100%',
                    borderRadius: 3,
                    background:
                      'linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.9) 100%)',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Matrix Controls
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Toggle between deterministic per-task variation and the raw fixed QWS values.
                    When variation is disabled, the task slices become identical.
                  </Typography>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={taskSpecificVariationEnabled}
                        onChange={(event) =>
                          onTaskSpecificVariationChange?.(event.target.checked)
                        }
                      />
                    }
                    label="Task-specific QoS variation"
                    sx={{ mb: 1 }}
                  />

                  <TextField
                    label="Matrix Seed"
                    type="number"
                    value={seedInput}
                    onChange={(event) => setSeedInput(event.target.value)}
                    fullWidth
                    error={seedIsInvalid}
                    disabled={!taskSpecificVariationEnabled}
                    helperText={
                      seedIsInvalid
                        ? 'Enter a valid numeric seed.'
                        : taskSpecificVariationEnabled
                          ? 'Apply a fixed seed to keep preview and optimization aligned.'
                          : 'Seed input is disabled because variation is off.'
                    }
                    inputProps={{ step: 1 }}
                  />

                  <Box sx={{ display: 'flex', gap: 1.25, mt: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      onClick={handleApplySeed}
                      disabled={!taskSpecificVariationEnabled || seedIsInvalid || seedIsApplied}
                    >
                      Apply Seed
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CasinoIcon />}
                      onClick={handleRandomizeSeed}
                      disabled={!taskSpecificVariationEnabled}
                    >
                      Randomize
                    </Button>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Matrix Slice
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    The full matrix is task x service x QoS property. This preview shows one task slice
                    at a time to keep the interface readable.
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      borderRadius: 2,
                      bgcolor: 'rgba(15, 23, 42, 0.03)',
                    }}
                  >
                    <IconButton
                      aria-label="Previous task"
                      onClick={() => handleTaskStep(-1)}
                      disabled={taskIndex === 0}
                      size="small"
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <TextField
                      label="Task"
                      type="number"
                      value={taskIndex + 1}
                      onChange={(event) => {
                        const nextTask = Number(event.target.value);
                        if (!Number.isFinite(nextTask)) {
                          return;
                        }
                        setTaskIndex(
                          Math.min(Math.max(Math.trunc(nextTask) - 1, 0), totalTasks - 1)
                        );
                      }}
                      size="small"
                      sx={{ width: 124 }}
                      inputProps={{ min: 1, max: totalTasks, step: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                      of {totalTasks}
                    </Typography>
                    <IconButton
                      aria-label="Next task"
                      onClick={() => handleTaskStep(1)}
                      disabled={taskIndex >= totalTasks - 1}
                      size="small"
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} lg={8}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    backgroundColor: 'rgba(255,255,255,0.94)',
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', md: 'center' },
                      flexDirection: { xs: 'column', md: 'row' },
                      gap: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Task {taskIndex + 1} Matrix Slice
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {taskSpecificVariationEnabled
                          ? `Showing ${pagedRows.length} of ${previewRows.length} services for the active task.`
                          : `Showing ${pagedRows.length} of ${previewRows.length} services using the raw fixed QWS values.`}
                      </Typography>
                    </Box>
                    <Chip
                      color="primary"
                      variant="outlined"
                      label={
                        taskSpecificVariationEnabled ? `Seed ${effectiveSeed}` : 'Variation Off'
                      }
                    />
                  </Box>

                  <TableContainer sx={{ maxHeight: 520 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 84, fontWeight: 700 }}>Service #</TableCell>
                          <TableCell sx={{ minWidth: 260, fontWeight: 700 }}>Service</TableCell>
                          {PROPERTY_SHORT_LABELS.map((shortLabel, index) => (
                            <Tooltip key={shortLabel} title={QWS_PROPERTY_LABELS[index]} arrow>
                              <TableCell
                                align="right"
                                sx={{ minWidth: 92, fontWeight: 700 }}
                              >
                                {shortLabel}
                              </TableCell>
                            </Tooltip>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedRows.map((row) => (
                          <TableRow key={`${row.serviceIndex}-${effectiveSeed}-${taskIndex}`} hover>
                            <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                              {row.serviceIndex + 1}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 280 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {row.serviceName}
                              </Typography>
                              {row.wsdlUrl && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: 'block',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                  title={row.wsdlUrl}
                                >
                                  {row.wsdlUrl}
                                </Typography>
                              )}
                            </TableCell>
                            {row.values.map((value, valueIndex) => (
                              <TableCell
                                key={`${row.serviceIndex}-${valueIndex}`}
                                align="right"
                                sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
                              >
                                {formatMatrixValue(value)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    count={previewRows.length}
                    page={page}
                    onPageChange={(_, nextPage) => setPage(nextPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </Paper>
              </Grid>
            </Grid>

            <Alert severity={taskSpecificVariationEnabled ? 'info' : 'success'}>
              {taskSpecificVariationEnabled
                ? 'Abbreviations: RT = Response Time, AV = Availability, TH = Throughput, SU = Successability, RE = Reliability, CO = Compliance, BP = Best Practices, LA = Latency, DOC = Documentation.'
                : 'Variation is off. Each task now uses the original QWS values, so changing the task index does not change the matrix slice.'}
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default QosMatrixModal;
